// FDA National Drug Code (NDC) API Service
// Provides access to real FDA medication database

export interface FDAMedication {
  ndc_code: string;
  product_name: string;
  generic_name?: string;
  manufacturer?: string;
  dosage_form?: string;
  route?: string;
  strength?: string;
  active_ingredients?: string[];
  brand_name?: string;
}

export interface FDASearchResult {
  results: FDAMedication[];
  total: number;
  limit: number;
  skip: number;
}

// Internal type definitions for FDA API responses
interface FDAIngredient {
  name: string;
  strength?: string;
}

interface FDAResult {
  product_ndc?: string;
  brand_name?: string;
  generic_name?: string;
  labeler_name?: string;
  dosage_form?: string;
  route?: string[];
  active_ingredients?: FDAIngredient[];
}

class FDAApiService {
  private readonly baseUrl = 'https://api.fda.gov/drug/ndc.json';
  private readonly apiKey = 'DEMO_KEY'; // Replace with real API key in production

  async searchMedications(query: string, limit: number = 20): Promise<FDASearchResult> {
    try {
      // Clean and format search query
      const searchQuery = query.trim().replace(/\s+/g, '+');
      
      // Search in product name, generic name, and brand name
      const searchFields = [
        `brand_name:"${searchQuery}"`,
        `generic_name:"${searchQuery}"`,
        `product_ndc:"${searchQuery}"`,
        `substance_name:"${searchQuery}"`
      ].join('+OR+');

      const url = `${this.baseUrl}?api_key=${this.apiKey}&search=${encodeURIComponent(searchFields)}&limit=${limit}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`FDA API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      return {
        results: this.formatMedications(data.results || []),
        total: data.meta?.results?.total || 0,
        limit: data.meta?.results?.limit || limit,
        skip: data.meta?.results?.skip || 0
      };
    } catch (error) {
      console.error('FDA API search error:', error);
      // Return fallback data if API fails
      return this.getFallbackMedications(query);
    }
  }

  async getMedicationByNDC(ndcCode: string): Promise<FDAMedication | null> {
    try {
      const url = `${this.baseUrl}?api_key=${this.apiKey}&search=product_ndc:"${ndcCode}"&limit=1`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`FDA API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const results = this.formatMedications(data.results || []);
      
      return results.length > 0 ? results[0] : null;
    } catch (error) {
      console.error('FDA API NDC lookup error:', error);
      return null;
    }
  }

  private formatMedications(rawResults: FDAResult[]): FDAMedication[] {
    return rawResults.map(item => ({
      ndc_code: item.product_ndc || '',
      product_name: item.brand_name || item.generic_name || 'Unknown Product',
      generic_name: item.generic_name,
      manufacturer: item.labeler_name,
      dosage_form: item.dosage_form,
      route: item.route?.[0],
      strength: item.active_ingredients?.[0]?.strength,
      active_ingredients: item.active_ingredients?.map((ing: FDAIngredient) => ing.name) || [],
      brand_name: item.brand_name
    }));
  }

  private getFallbackMedications(query: string): FDASearchResult {
    // Comprehensive fallback medication database
    const fallbackMeds: FDAMedication[] = [
      // Diabetes medications
      {
        ndc_code: '0078-0551-05',
        product_name: 'Glucophage',
        generic_name: 'Metformin HCl',
        manufacturer: 'Bristol-Myers Squibb',
        dosage_form: 'Tablet',
        route: 'Oral',
        strength: '500 mg',
        active_ingredients: ['Metformin Hydrochloride'],
        brand_name: 'Glucophage'
      },
      {
        ndc_code: '0078-0552-05',
        product_name: 'Glucophage XR',
        generic_name: 'Metformin HCl Extended Release',
        manufacturer: 'Bristol-Myers Squibb',
        dosage_form: 'Tablet, Extended Release',
        route: 'Oral',
        strength: '750 mg',
        active_ingredients: ['Metformin Hydrochloride'],
        brand_name: 'Glucophage XR'
      },
      // Cardiovascular medications
      {
        ndc_code: '0071-0155-23',
        product_name: 'Lipitor',
        generic_name: 'Atorvastatin Calcium',
        manufacturer: 'Pfizer',
        dosage_form: 'Tablet',
        route: 'Oral',
        strength: '20 mg',
        active_ingredients: ['Atorvastatin Calcium'],
        brand_name: 'Lipitor'
      },
      {
        ndc_code: '0173-0344-02',
        product_name: 'Lisinopril',
        generic_name: 'Lisinopril',
        manufacturer: 'Lupin Pharmaceuticals',
        dosage_form: 'Tablet',
        route: 'Oral',
        strength: '10 mg',
        active_ingredients: ['Lisinopril'],
        brand_name: 'Lisinopril'
      },
      {
        ndc_code: '50090-1587-0',
        product_name: 'Amlodipine',
        generic_name: 'Amlodipine Besylate',
        manufacturer: 'A-S Medication Solutions',
        dosage_form: 'Tablet',
        route: 'Oral',
        strength: '5 mg',
        active_ingredients: ['Amlodipine Besylate'],
        brand_name: 'Amlodipine'
      },
      // Mental Health medications
      {
        ndc_code: '65862-0506-90',
        product_name: 'Sertraline',
        generic_name: 'Sertraline HCl',
        manufacturer: 'Aurobindo Pharma',
        dosage_form: 'Tablet',
        route: 'Oral',
        strength: '50 mg',
        active_ingredients: ['Sertraline Hydrochloride'],
        brand_name: 'Sertraline'
      },
      {
        ndc_code: '0378-4730-91',
        product_name: 'Trazodone',
        generic_name: 'Trazodone HCl',
        manufacturer: 'Mylan Pharmaceuticals',
        dosage_form: 'Tablet',
        route: 'Oral',
        strength: '100 mg',
        active_ingredients: ['Trazodone Hydrochloride'],
        brand_name: 'Trazodone'
      },
      // Common medications
      {
        ndc_code: '0093-2100-01',
        product_name: 'Omeprazole',
        generic_name: 'Omeprazole',
        manufacturer: 'Teva Pharmaceuticals',
        dosage_form: 'Capsule',
        route: 'Oral',
        strength: '20 mg',
        active_ingredients: ['Omeprazole'],
        brand_name: 'Omeprazole'
      },
      {
        ndc_code: '0378-6155-93',
        product_name: 'Simvastatin',
        generic_name: 'Simvastatin',
        manufacturer: 'Mylan Pharmaceuticals',
        dosage_form: 'Tablet',
        route: 'Oral',
        strength: '40 mg',
        active_ingredients: ['Simvastatin'],
        brand_name: 'Simvastatin'
      },
      {
        ndc_code: '0591-3226-01',
        product_name: 'Gabapentin',
        generic_name: 'Gabapentin',
        manufacturer: 'Actavis Pharma',
        dosage_form: 'Capsule',
        route: 'Oral',
        strength: '300 mg',
        active_ingredients: ['Gabapentin'],
        brand_name: 'Gabapentin'
      }
    ];

    const filtered = fallbackMeds.filter(med => 
      med.product_name.toLowerCase().includes(query.toLowerCase()) ||
      med.generic_name?.toLowerCase().includes(query.toLowerCase()) ||
      med.active_ingredients?.some(ing => ing.toLowerCase().includes(query.toLowerCase()))
    );

    return {
      results: filtered,
      total: filtered.length,
      limit: 20,
      skip: 0
    };
  }
}

export const fdaApi = new FDAApiService(); 
